import React, { useState, useMemo, useEffect, useRef } from "react";
import { Table, Button, Collapse, OverlayTrigger, Tooltip } from "react-bootstrap";
import {
  FaChevronLeft,
  FaChevronRight,
  FaChevronDown,
  FaChevronUp,
} from "react-icons/fa";
import { useTranslation } from "react-i18next";
import ProjectAllocationForm from "../ProjectAllocationForm/ProjectAllocationForm";
import { LOCATION_STATUS, LOCATION_STATUS_STYLES } from "../../../utils/workLocation";
import "./TimeBalanceTable.css";

const toLocalDateString = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const isInProgress = (row) => {
  const hasTime = (value) => !!value && value !== "--:--";
  return hasTime(row.clock_in) && !hasTime(row.clock_out);
};

export const LocationBadge = ({ row }) => {
  const { t } = useTranslation();
  if (!row.location_status && !row.location_alert) return null;

  const status = row.location_alert ? LOCATION_STATUS.OTHER : row.location_status;
  const style = LOCATION_STATUS_STYLES[status] || LOCATION_STATUS_STYLES.unknown;
  const details = [
    row.location_alert && t("components.TimeBalanceTable.location.alert"),
    row.location_note && t("components.TimeBalanceTable.location.note", { note: row.location_note }),
  ].filter(Boolean);

  const badge = (
    <span
      data-testid={`location-badge-${row.date}`}
      onClick={(e) => e.stopPropagation()}
      style={{
        backgroundColor: style.bg,
        color: style.color,
        padding: "2px 8px",
        borderRadius: "12px",
        fontSize: "0.72rem",
        fontWeight: 600,
        whiteSpace: "nowrap",
        cursor: details.length ? "help" : "default",
      }}
    >
      {row.location_alert && "⚠ "}
      {t(`components.TimeBalanceTable.location.${status}`)}
    </span>
  );

  if (!details.length) return badge;
  return (
    <OverlayTrigger
      placement="top"
      overlay={
        <Tooltip id={`tooltip-location-${row.id}`}>
          {details.map((line) => (
            <div key={line}>{line}</div>
          ))}
        </Tooltip>
      }
    >
      {badge}
    </OverlayTrigger>
  );
};

const toHHMM = (val) => {
  if (!val) return null;
  return String(val).substring(0, 5);
};

const normalizePauses = (raw) => {
  if (!Array.isArray(raw) || raw.length === 0) return [];
  return raw.map((p) => ({
    start: toHHMM(p.start),
    end: toHHMM(p.end),
    reason: p.reason || '',
  }));
};

const TimeBalanceTable = ({
  historyData,
  currentDate = new Date(),
  userId,
  setCurrentDate = () => {},
  viewMode = "month",
  setViewMode = () => {},
  onSaveDailyAudit,
}) => {
  const { t, i18n } = useTranslation(); 
  const [expandedRowId, setExpandedRowId] = useState(null);
  const focusedRowRef = useRef(null);
  const hasAutoFocused = useRef(false);

  const locale = i18n.language === 'pt' ? 'pt-PT' : 'en-US';

  const getStartOfWeek = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(d.setDate(diff));
  };
  

  const daysInView = useMemo(() => {
    let start, end;

    if (viewMode === "day") {
      start = new Date(currentDate);
      end = new Date(currentDate);
    } else if (viewMode === "week") {
      start = getStartOfWeek(currentDate);
      end = new Date(start.getTime() + 6 * 24 * 60 * 60 * 1000);
    } else { 
      start = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
      end = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
    }

    const days = [];
    let d = new Date(start);
    
    while (d <= end) {
      const dateStr = toLocalDateString(d);
      const existingRow = historyData?.find((r) => r.date === dateStr);
      const isWeekend = d.getDay() === 0 || d.getDay() === 6;

      if (existingRow) {
        days.push({
          ...existingRow,
          clock_in: toHHMM(existingRow.clock_in),
          clock_out: toHHMM(existingRow.clock_out),
          pauses: normalizePauses(existingRow.pauses),
        });
      } else {
        days.push({
          id: `virtual-${dateStr}`,
          user_id: userId,
          date: dateStr,
          worked_minutes: 0,
          expected_minutes: isWeekend ? 0 : 480,
          clock_in: "--:--",
          clock_out: "--:--",
          pauses: [{ start: "13:00", end: "14:00", reason: t("TimeBalanceTable.lunch", "Almoço") }],
          projects: [],
          balance_minutes: 0,
          month_closure_id: null,
          approved: false
        });
      }
      d.setDate(d.getDate() + 1);
    }
    
    return days;
  }, [historyData, currentDate, viewMode, userId, t]);
  
  // Opens the running shift (or today) once, so the current day can be edited straight away
  useEffect(() => {
    if (hasAutoFocused.current) return;
    const target =
      daysInView.find((row) => isInProgress(row)) ||
      daysInView.find((row) => row.date === toLocalDateString(new Date()));
    if (!target) return;
    hasAutoFocused.current = true;
    setExpandedRowId(target.id);
  }, [daysInView]);

  useEffect(() => {
    if (expandedRowId && focusedRowRef.current?.scrollIntoView) {
      focusedRowRef.current.scrollIntoView({ block: "center", behavior: "smooth" });
    }
  }, [expandedRowId]);

  const goToBack = () => {
    const newDate = new Date(currentDate);
    if (viewMode === "day") newDate.setDate(newDate.getDate() - 1);
    if (viewMode === "week") newDate.setDate(newDate.getDate() - 7);
    if (viewMode === "month") newDate.setMonth(newDate.getMonth() - 1);
    setCurrentDate(newDate);
  };

  const goToNext = () => {
    const newDate = new Date(currentDate);
    if (viewMode === "day") newDate.setDate(newDate.getDate() + 1);
    if (viewMode === "week") newDate.setDate(newDate.getDate() + 7);
    if (viewMode === "month") newDate.setMonth(newDate.getMonth() + 1);
    setCurrentDate(newDate);
  };

  const goToCurrent = () => setCurrentDate(new Date());

  const getLabel = () => {
    if (viewMode === "day") {
      const dateStr = currentDate.toLocaleDateString(locale, {
        day: "2-digit",
        month: "long",
        year: "numeric",
      });
      return dateStr.charAt(0).toUpperCase() + dateStr.slice(1);
    }

    if (viewMode === "week") {
      const start = getStartOfWeek(currentDate);
      const end = new Date(start);
      end.setDate(end.getDate() + 6);

      const startDay = start.getDate();
      const endDay = end.getDate();
      const monthStr = end.toLocaleDateString(locale, { month: "short" });
      const yearStr = end.getFullYear();
      
      const toStr = t("TimeBalanceTable.date_format.to", "a");
      const ofStr = t("TimeBalanceTable.date_format.of", "de");

      return `${startDay} ${toStr} ${endDay} ${ofStr} ${monthStr} ${yearStr}`;
    }

    if (viewMode === "month") {
      const monthStr = currentDate.toLocaleDateString(locale, { month: "long" });
      const yearStr = currentDate.getFullYear();
      return `${monthStr.charAt(0).toUpperCase() + monthStr.slice(1)} ${yearStr}`;
    }
  };

  const formatMinutesToReadable = (totalMinutes) => {
    if (totalMinutes === null || totalMinutes === undefined) return "0h 00m";
    const absMinutes = Math.abs(totalMinutes);
    const hrs = Math.floor(absMinutes / 60);
    const mins = absMinutes % 60;
    return `${hrs}h ${mins.toString().padStart(2, "0")}m`; 
  };

  const formatDateDisplay = (dateString) => {
    const d = new Date(dateString);
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    return `${day}/${month}`;
  };

  const getStatusBadge = (row) => {
    const isVirtual = String(row.id).startsWith("virtual-");
    
    if (isVirtual) {
      const isWeekend = new Date(row.date).getDay() === 0 || new Date(row.date).getDay() === 6;
      if (isWeekend) return { text: t("TimeBalanceTable.status.weekend", "Fim de semana"), style: { bg: "var(--gray-100)", color: "var(--text-subtle)" } };
      return { text: t("TimeBalanceTable.status.pending_fill", "Por preencher"), style: { bg: "rgba(234, 179, 8, 0.1)", color: "#92400e" } };
    }
    
    if (isInProgress(row)) {
      return { text: t("components.TimeBalanceTable.status.in_progress"), style: { bg: "rgba(59, 130, 246, 0.1)", color: "#1d4ed8" } };
    }

    if (row.worked_minutes === 0 && row.expected_minutes > 0) {
      return { text: t("TimeBalanceTable.status.missing", "Falta"), style: { bg: "rgba(239, 68, 68, 0.1)", color: "#ef4444" } };
    }
    
    if (row.approved === true || row.month_closure_id) {
      return { text: t("TimeBalanceTable.status.validated", "Validado"), style: { bg: "rgba(16, 185, 129, 0.1)", color: "#10b981" } };
    }
    
    return { text: t("TimeBalanceTable.status.pending", "Pendente"), style: { bg: "rgba(229, 231, 235, 1)", color: "#64748b" } };
  };
  
  return (
    <div className="atlar-card p-4 w-100">

      <div className="d-flex flex-wrap justify-content-between align-items-center mb-4 gap-2" style={{ width: "100%" }}>
        <div className="d-flex align-items-center gap-1" style={{ flex: "1 1 auto", minWidth: 0 }}>
          <Button
            variant="light" size="sm" onClick={goToBack}
            className="border-0 shadow-sm txt-main bg-surface d-flex align-items-center justify-content-center p-0"
            style={{ borderRadius: "6px", width: "26px", height: "26px", flexShrink: 0 }}
          >
            <FaChevronLeft size={10} />
          </Button>
          <span className="fw-bold txt-main text-center px-2" style={{ fontSize: "0.95rem", minWidth: "150px" }}>
            {getLabel()}
          </span>
          <Button
            variant="light" size="sm" onClick={goToNext}
            className="border-0 shadow-sm txt-main bg-surface d-flex align-items-center justify-content-center p-0"
            style={{ borderRadius: "6px", width: "26px", height: "26px", flexShrink: 0 }}
          >
            <FaChevronRight size={10} />
          </Button>
          <Button
            variant="outline-secondary" size="sm" onClick={goToCurrent}
            className="ms-2 fw-bold py-0 px-2 bg-surface"
            style={{ borderRadius: "6px", height: "26px", fontSize: "0.75rem", flexShrink: 0 }}
          >
            {t("components.Calendar.today", "Hoje")}
          </Button>
        </div>

        <div className="d-flex gap-1 bg-surface p-1 rounded-3 border" style={{ flexShrink: 0 }}>
          {[
            { id: "day",   label: t("components.Calendar.views.day",   "Dia") },
            { id: "week",  label: t("components.Calendar.views.week",  "Semana") },
            { id: "month", label: t("components.Calendar.views.month", "Mês") },
          ].map((view) => (
            <Button
              key={view.id}
              variant="transparent" size="sm"
              onClick={() => setViewMode(view.id)}
              className="fw-bold border-0 shadow-none m-0"
              style={{
                backgroundColor: viewMode === view.id ? "white" : "transparent",
                color: viewMode === view.id ? "var(--brand-primary, #5710B2)" : "var(--text-secondary, #64748b)",
                boxShadow: viewMode === view.id ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
                borderRadius: "4px",
                padding: "2px 12px",
                fontSize: "0.80rem",
              }}
            >
              {view.label}
            </Button>
          ))}
        </div>
      </div>
      <div className="custom-scrollbar" style={{ overflowX: "auto" }}>
        <Table responsive className="align-middle mb-0 custom-expandable-table">
          <thead>
            <tr style={{ borderBottom: "var(--border-thin)" }}>
              <th style={{ width: "30px" }}></th>
              <th className="txt-subtle fw-semibold border-0 pb-3">{t("TimeBalanceTable.headers.date", "Data")}</th>
              <th className="txt-subtle fw-semibold border-0 pb-3">{t("TimeBalanceTable.headers.clock", "Entrada / Pausa / Saída")}</th>
              <th className="txt-subtle fw-semibold border-0 pb-3">{t("TimeBalanceTable.headers.worked", "Total Trabalhado")}</th>
              <th className="txt-subtle fw-semibold border-0 pb-3">{t("TimeBalanceTable.headers.balance", "Saldo")}</th>
              <th className="txt-subtle fw-semibold border-0 pb-3 text-end">{t("TimeBalanceTable.headers.status", "Status")}</th>
            </tr>
          </thead>
          <tbody>
            {daysInView.map((row) => {
              const badge = getStatusBadge(row);
              const isPositive = row.balance_minutes > 0;
              const isNeutral = row.balance_minutes === 0;
              const isExpanded = expandedRowId === row.id;
              const isLocked = !!row.month_closure_id;
              const isVirtual = String(row.id).startsWith("virtual-");

              const totalBreakMinutes = (row.pauses || []).reduce((sum, p) => {
                if (!p.start || !p.end) return sum;
                const [sh, sm] = p.start.split(':').map(Number);
                const [eh, em] = p.end.split(':').map(Number);
                const diff = (eh * 60 + em) - (sh * 60 + sm);
                return sum + (diff < 0 ? diff + 1440 : diff);
              }, 0);

              return (
                <React.Fragment key={row.id}>
                  <tr
                    ref={isExpanded ? focusedRowRef : undefined}
                    data-testid={`time-balance-row-${row.date}`}
                    onClick={() => setExpandedRowId(isExpanded ? null : row.id)}
                    className={`expandable-row-header ${isExpanded ? "is-expanded" : ""}`}
                    style={{
                      cursor: "pointer",
                      borderBottom: "1px solid var(--gray-100)",
                      opacity: isVirtual ? 0.65 : 1,
                    }}
                  >
                   <td>
                    {isExpanded ? (
                      <FaChevronUp size={12} style={{ color: "var(--text-secondary)" }} />
                    ) : (
                      <FaChevronDown size={12} style={{ color: "var(--text-secondary)" }} />
                    )}
                  </td>
                    <td className="txt-main fw-semibold">{formatDateDisplay(row.date)}</td>
                    <td>
                      <div className="d-flex align-items-center gap-2 txt-main">
                        <span className="fw-semibold">{row.clock_in || "--:--"}</span>
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip id={`tooltip-break-${row.id}`}>
                              <div style={{ textAlign: "left" }}>
                                <strong>{t("TimeBalanceTable.tooltip.breaks", "Pausas:")}</strong><br />
                                {row.pauses && row.pauses.length > 0 ? (
                                  row.pauses.map((p, index) => (
                                    <div key={index} className="small py-1">
                                      {p.start || "--:--"} – {p.end || "--:--"}
                                      {p.reason ? ` (${p.reason})` : ""}
                                    </div>
                                  ))
                                ) : (
                                  <div className="small">{t("TimeBalanceTable.tooltip.no_breaks", "Sem pausas registadas")}</div>
                                )}                                
                                <div className="mt-2 pt-1"> 
                                  <strong>{t("TimeBalanceTable.tooltip.total", "Total:")}</strong> {formatMinutesToReadable(totalBreakMinutes)}
                                </div>
                              </div>
                            </Tooltip>
                          }
                        >
                          <span
                            className="badge bg-secondary-subtle text-secondary rounded-circle d-inline-flex align-items-center justify-content-center"
                            style={{ width: "20px", height: "20px", cursor: "help", fontSize: "11px" }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            ?
                          </span>
                        </OverlayTrigger>

                        <span className="txt-subtle">➔</span>
                        <span className="fw-semibold">{row.clock_out || "--:--"}</span>
                        <LocationBadge row={row} />
                      </div>
                    </td>
                    <td className="txt-main">{formatMinutesToReadable(row.worked_minutes)}</td>
                    <td className={`fw-bold ${isNeutral ? "txt-subtle" : isPositive ? "txt-success" : "txt-danger"}`}>
                      {isNeutral ? "" : isPositive ? "+ " : "- "}
                      {formatMinutesToReadable(row.balance_minutes)}
                    </td>
                    <td className="text-end">
                      <span style={{
                        backgroundColor: badge.style.bg,
                        color: badge.style.color,
                        padding: "4px 12px",
                        borderRadius: "20px",
                        fontSize: "var(--text-small)",
                        fontWeight: "600",
                      }}>
                        {badge.text}
                      </span>
                    </td>
                  </tr>

                  <tr>
                    <td colSpan="6" className="p-0 border-0">
                      <Collapse in={isExpanded}>
                        <div className="bg-surface p-4 border-bottom">
                          <ProjectAllocationForm
                            row={row}
                            isLocked={isLocked}
                            onSave={(payload) => onSaveDailyAudit(payload)}
                          />
                        </div>
                      </Collapse>
                    </td>
                  </tr>
                </React.Fragment>
              );
            })}
          </tbody>
        </Table>
      </div>
    </div>
  );
};

export default TimeBalanceTable;